/**
 * Sportal API client. Lives at api.sportalapp.com (Worker + Hono).
 * Reads JWT from sessionStorage (sportal.session) so /api/me works after login.
 */
const API_BASE = import.meta.env.VITE_API_BASE ?? "https://api.sportalapp.com";

const SESSION_KEY = "sportal.session";

export type Session = {
  access_token: string;
  refresh_token?: string | null;
  expires_at?: number | null;
  user?: { id: string; email?: string | null } | null;
};

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function setSession(s: Session | null) {
  try {
    if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    else localStorage.removeItem(SESSION_KEY);
  } catch {}
  // Fire a custom event so any subscribed component can re-render.
  try { window.dispatchEvent(new Event("sportal:session")); } catch {}
}

export function useSession() {
  // Lightweight hook — re-runs on storage events.
  const [s, set] = (window as any).React?.useState ? (window as any).React.useState(getSession()) : [getSession(), null];
  return s;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const sess = getSession();
  const headers = new Headers(init.headers);
  if (!headers.has("content-type") && init.body) headers.set("content-type", "application/json");
  if (sess?.access_token) headers.set("authorization", `Bearer ${sess.access_token}`);
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const txt = await res.text();
  let json: any = null;
  try { json = txt ? JSON.parse(txt) : null; } catch { /* not json */ }
  if (!res.ok) {
    const msg = (json && (json.message || json.error)) || txt || `HTTP ${res.status}`;
    const err: any = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return json as T;
}

// --- public reads -----------------------------------------------------
export type Club = {
  id: string; slug: string; name: string; city?: string; province?: string;
  description?: string; photo_url?: string; amenities?: string[];
  is_bookable?: boolean; featured?: boolean; currency?: string;
  contact_phone?: string; contact_email?: string;
  cancellation_hours?: number; price_tier?: number; rating?: number;
};
export type Slot = {
  id: string; club_id: string; start_time: string; duration_min: number;
  max_players: number; green_fee_cents: number; status: string; tee: number;
  bookings_count?: number; seats_remaining?: number;
};
export type Booking = {
  id: string; slot_id: string; club_id: string; players: number;
  total_cents: number; status: string; payment_status: string;
  guest_names?: string[]; notes?: string | null; created_at: string;
  slot?: { start_time: string; duration_min: number; tee: number };
  club?: { name: string; slug: string; city?: string; province?: string; photo_url?: string; contact_phone?: string };
};

export const api = {
  listClubs: (opts: { bookable?: boolean; featured?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.bookable) q.set("bookable", "true");
    if (opts.featured) q.set("featured", "true");
    return request<Club[]>(`/api/clubs${q.toString() ? "?" + q : ""}`);
  },
  getClub: (idOrSlug: string) => request<Club>(`/api/clubs/${idOrSlug}`),
  listSlots: (idOrSlug: string, date?: string) => {
    const q = new URLSearchParams();
    if (date) q.set("date", date);
    q.set("tz", "Africa/Johannesburg");
    return request<{ club: Club; date: string; tz: string; slots: Slot[]; not_bookable?: boolean }>(
      `/api/public/clubs/${idOrSlug}/slots?${q}`
    );
  },
  signup: (body: { email: string; password: string; display_name?: string; phone?: string }) =>
    request<Session>(`/api/public/signup`, { method: "POST", body: JSON.stringify(body) }),
  createBooking: (body: {
    slot_id: string; players: number; guest_names?: string[]; notes?: string | null;
    email?: string; password?: string; display_name?: string; phone?: string;
  }) =>
    request<{ booking: Booking; session?: Session | null }>(
      `/api/public/bookings`, { method: "POST", body: JSON.stringify(body) }
    ),
  myBookings: () => request<Booking[]>(`/api/me/bookings`),
  cancelBooking: (id: string) =>
    request<Booking>(`/api/me/bookings/${id}/cancel`, { method: "POST" }),
};

// --- supabase direct login (avoids round-tripping through our API) ----
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? "https://ymvqginbjbtupogmhgrw.supabase.co";
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY
  ?? "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InltdnFnaW5iamJ0dXBvZ21oZ3J3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODAzMjk2OTksImV4cCI6MjA5NTkwNTY5OX0.T5K153JxWu_SfxG74a3HmKkECwMq_IUqasfJZ78twQI";

export const sb = createClient(SUPABASE_URL, SUPABASE_ANON, {
  auth: { persistSession: false, autoRefreshToken: false },
});

export async function login(email: string, password: string): Promise<Session> {
  const { data, error } = await sb.auth.signInWithPassword({ email, password });
  if (error) throw error;
  const s: Session = {
    access_token: data.session?.access_token ?? "",
    refresh_token: data.session?.refresh_token ?? null,
    expires_at: data.session?.expires_at ?? null,
    user: data.user ? { id: data.user.id, email: data.user.email } : null,
  };
  setSession(s);
  return s;
}

export function logout() { setSession(null); }

// --- formatting helpers -----------------------------------------------
export function formatZar(cents: number) {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency", currency: "ZAR", maximumFractionDigits: 0,
  }).format(cents / 100);
}
export function formatTime(iso: string) {
  return new Intl.DateTimeFormat("en-ZA", {
    timeZone: "Africa/Johannesburg",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(new Date(iso));
}
export function formatDate(iso: string) {
  return new Intl.DateTimeFormat("en-ZA", {
    timeZone: "Africa/Johannesburg",
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  }).format(new Date(iso));
}
export function formatShortDate(iso: string) {
  return new Intl.DateTimeFormat("en-ZA", {
    timeZone: "Africa/Johannesburg",
    weekday: "short", day: "numeric", month: "short",
  }).format(new Date(iso));
}
export function todayYmd(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Johannesburg",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}
export function addDaysYmd(ymd: string, days: number): string {
  const parts = ymd.split("-").map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}
